import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Megaphone, Clock, ClipboardList, MapPin, Calendar,
  Wallet, Banknote, FileText, Users, TestTube, LogOut, ChevronLeft,
  ChevronRight, Menu, X, Zap, HandCoins, TrendingUp, Palmtree,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, tid: "nav-admin-dashboard", exact: true },
      { to: "/admin/pengumuman", label: "Pengumuman", icon: Megaphone, tid: "nav-admin-pengumuman" },
    ],
  },
  {
    title: "Operasional",
    items: [
      { to: "/admin/absen", label: "Absen", icon: Clock, tid: "nav-admin-absen" },
      { to: "/admin/rekap", label: "Rekap", icon: ClipboardList, tid: "nav-admin-rekap" },
      { to: "/admin/performa", label: "Performa Pegawai", icon: TrendingUp, tid: "nav-admin-performa" },
      { to: "/admin/lokasi", label: "Lokasi", icon: MapPin, tid: "nav-admin-lokasi" },
      { to: "/admin/izin", label: "Izin/Sakit", icon: Calendar, tid: "nav-admin-izin" },
      { to: "/admin/periode-bebas", label: "Periode Bebas", icon: Palmtree, tid: "nav-admin-periode-bebas" },
      { to: "/admin/multiplier", label: "Multiplier Lembur", icon: Zap, tid: "nav-admin-multiplier" },
    ],
  },
  {
    title: "Finansial",
    items: [
      { to: "/admin/kasbon", label: "Kasbon", icon: Wallet, tid: "nav-admin-kasbon" },
      { to: "/admin/pinjaman", label: "Pinjaman", icon: Banknote, tid: "nav-admin-pinjaman" },
      { to: "/admin/upah-phl", label: "Upah PHL", icon: HandCoins, tid: "nav-admin-upah-phl" },
      { to: "/admin/slip", label: "Slip Gaji", icon: FileText, tid: "nav-admin-slip" },
    ],
  },
  {
    title: "Sistem",
    items: [
      { to: "/admin/pegawai", label: "Pegawai", icon: Users, tid: "nav-admin-pegawai" },
      { to: "/admin/simulasi", label: "Simulasi", icon: TestTube, tid: "nav-admin-simulasi" },
    ],
  },
];

const NavPanel = ({ mobile, collapsed, setCollapsed, setMobileOpen, isActive, user, logout }) => (
  <aside
    className={`${mobile ? "" : "hidden lg:flex"} flex-col bg-white border-r border-zinc-200 h-screen sticky top-0 transition-all duration-200 ${collapsed && !mobile ? "w-16" : "w-60"} ${mobile ? "w-64" : ""}`}
    data-testid="admin-sidebar"
  >
    <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-200 shrink-0">
      {(!collapsed || mobile) && (
        <div className="min-w-0">
          <p className="font-display font-bold text-primary text-sm">RelayLab</p>
          <p className="text-[0.6rem] text-zinc-500 uppercase tracking-widest">Admin Panel</p>
        </div>
      )}
      {!mobile ? (
        <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="h-8 w-8 shrink-0" data-testid="btn-toggle-sidebar">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      ) : (
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="h-8 w-8">
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
    <nav className="flex-1 overflow-y-auto py-3">
      {NAV_SECTIONS.map((section) => (
        <div key={section.title} className="mb-4 px-2">
          {(!collapsed || mobile) && (
            <p className="overline text-[0.6rem] text-zinc-400 px-3 mb-1">{section.title}</p>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    title={collapsed && !mobile ? item.label : undefined}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      active ? "bg-primary text-white font-medium" : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                    data-testid={item.tid}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    {(!collapsed || mobile) && <span className="truncate">{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
    <div className="border-t border-zinc-200 p-2 shrink-0">
      {(!collapsed || mobile) && (
        <div className="px-3 py-2 mb-1">
          <p className="text-xs font-medium truncate">{user?.name}</p>
          <p className="text-[0.65rem] text-zinc-500 font-mono">{user?.employee_id}</p>
        </div>
      )}
      <button
        onClick={logout}
        title={collapsed && !mobile ? "Keluar" : undefined}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors"
        data-testid="btn-logout"
      >
        <LogOut className="w-4 h-4 shrink-0" />
        {(!collapsed || mobile) && <span>Keluar</span>}
      </button>
    </div>
  </aside>
);

export const AdminSidebar = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("admSidebarCollapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("admSidebarCollapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const isActive = (item) => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  const commonProps = { collapsed, setCollapsed, setMobileOpen, isActive, user, logout };

  return (
    <>
      <NavPanel mobile={false} {...commonProps} />
      <header className="lg:hidden bg-white border-b border-zinc-200 h-14 flex items-center justify-between px-4 sticky top-0 z-30">
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="btn-mobile-menu">
          <Menu className="w-5 h-5" />
        </Button>
        <div>
          <p className="font-display font-bold text-primary text-sm">RelayLab</p>
        </div>
        <div className="w-8" />
      </header>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-50" onClick={(e) => e.stopPropagation()}>
            <NavPanel mobile={true} {...commonProps} />
          </div>
        </div>
      )}
    </>
  );
};
